import { Box, Typography, Tooltip, Chip } from "@mui/material";
import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import FlagIcon from "@mui/icons-material/Flag";
import { useGetFileDataResultsQuery } from "../../useQuery";

const TableScroll = styled(Box)(() => ({
  overflowX: "auto",
  overflowY: "auto",
  maxHeight: "60vh",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
  "&::-webkit-scrollbar": { width: "6px", height: "6px" },
  "&::-webkit-scrollbar-track": { background: "#f3f4f6" },
  "&::-webkit-scrollbar-thumb": {
    background: "#d1d5db",
    borderRadius: "3px",
    "&:hover": { background: "#9ca3af" },
  },
}));

const baseTh = {
  padding: "12px 16px",
  fontWeight: 600,
  textAlign: "center",
  background: "#f9fafb",
  borderBottom: "1px solid #e5e7eb",
  borderRight: "1px solid #e5e7eb",
  minWidth: "120px",
  color: "#374151",
  fontSize: "12px",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const baseTd = {
  padding: "12px 16px",
  borderBottom: "1px solid #e5e7eb",
  borderRight: "1px solid #e5e7eb",
  textAlign: "center",
  fontSize: "13px",
  verticalAlign: "middle",
};

const StatusChip = ({ status }) => {
  if (!status)
    return (
      <Typography fontSize="12px" color="#d1d5db">
        —
      </Typography>
    );
  const isPassed = status === "PASS";
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "11px",
        backgroundColor: isPassed ? "#f0fdf4" : status === "FAIL" ? "#fef2f2" : "#f3f4f6",
        color: isPassed ? "#15803d" : status === "FAIL" ? "#dc2626" : "#6b7280",
        border: `1px solid ${isPassed ? "#dcfce7" : status === "FAIL" ? "#fecaca" : "#e5e7eb"}`,
      }}
    />
  );
};

const MultiRunTable = ({ tcrid }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { data, isLoading, isError, error } = useGetFileDataResultsQuery(
    tcrid,
    currentPage,
    itemsPerPage,
    { enabled: !!tcrid },
  );

  const runs = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;
  const totalPages = data?.totalPages ?? 0;
  const startIdx = (currentPage - 1) * itemsPerPage + 1;
  const endIdx = Math.min(currentPage * itemsPerPage, totalRecords);

  const hasStepsArray = runs.length > 0 && Array.isArray(runs[0].steps);
  const maxSteps = hasStepsArray ? Math.max(...runs.map((d) => d.steps?.length || 0)) : 0;
  const stepMilestones = hasStepsArray
    ? Array.from({ length: maxSteps }, (_, i) => runs[0]?.steps?.[i]?.milestone ?? null)
    : [];
  const stepNumbers = hasStepsArray
    ? Array.from({ length: maxSteps }, (_, i) => runs[0]?.steps?.[i]?.step_number ?? i + 1)
    : [];

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py="60px">
        <Typography fontSize="13px" color="#6b7280">
          Loading…
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py="60px">
        <Typography fontSize="13px" color="#dc2626" fontWeight={500}>
          {error?.message || "Failed to load"}
        </Typography>
      </Box>
    );
  }

  if (runs.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py="60px">
        <Typography fontSize="13px" color="#6b7280">
          No data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableScroll>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...baseTh, textAlign: "left" }}>
                <Box px="16px" py="12px">
                  Run
                </Box>
              </th>

              {hasStepsArray ? (
                <>
                  <th style={baseTh}>Status</th>
                  {Array.from({ length: maxSteps }, (_, i) => (
                    <th key={i} style={baseTh}>
                      <Box>
                        {stepMilestones[i] && (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap="3px"
                            mb="2px"
                          >
                            <FlagIcon sx={{ fontSize: 13, color: "#15803d" }} />
                            <Box
                              component="span"
                              sx={{
                                fontSize: "13px",
                                color: "#15803d",
                                fontWeight: 700,
                                textTransform: "uppercase",
                              }}
                            >
                              {stepMilestones[i]}
                            </Box>
                          </Box>
                        )}
                        <Box sx={{ fontSize: "10px", color: "#9ca3af", fontWeight: 400 }}>
                          Step {stepNumbers[i]}
                        </Box>
                      </Box>
                    </th>
                  ))}
                </>
              ) : (
                <>
                  <th style={baseTh}>Milestone</th>
                  <th style={baseTh}>Status</th>
                  <th style={baseTh}>Test Data</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {runs.map((runItem) => (
              <tr key={runItem.run}>
                <td style={{ ...baseTd, textAlign: "left" }}>
                  <Box px="16px" py="12px">
                    <Typography fontSize="13px" fontWeight={600} color="#111827">
                      {runItem.run}
                    </Typography>
                  </Box>
                </td>

                {hasStepsArray ? (
                  <>
                    <td style={baseTd}>
                      <StatusChip status={runItem.tc_status} />
                    </td>

                    {Array.from({ length: maxSteps }, (_, stepIdx) => {
                      const step = runItem.steps?.[stepIdx];
                      const testdata = step?.testdata ?? null;
                      const stepStatus = step?.step_status ?? null;
                      const isPassed = stepStatus === "PASS";
                      const isFailed = stepStatus === "FAIL";
                      const pillBg = isPassed ? "#f0fdf4" : isFailed ? "#fef2f2" : "#f0f4ff";
                      const pillBorder = isPassed
                        ? "1px solid #dcfce7"
                        : isFailed
                          ? "1px solid #fecaca"
                          : "1px solid #dbeafe";
                      const pillColor = isPassed ? "#15803d" : isFailed ? "#dc2626" : "#1e40af";
                      return (
                        <td key={stepIdx} style={baseTd}>
                          {testdata ? (
                            <Tooltip title={`${stepStatus ?? ""} · ${testdata}`} placement="top">
                              <Box
                                sx={{
                                  padding: "4px 8px",
                                  display: "inline-block",
                                  background: pillBg,
                                  border: pillBorder,
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  color: pillColor,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "140px",
                                  cursor: "pointer",
                                  "&:hover": { opacity: 0.85 },
                                }}
                              >
                                {testdata}
                              </Box>
                            </Tooltip>
                          ) : (
                            <Typography fontSize="12px" color="#d1d5db">
                              —
                            </Typography>
                          )}
                        </td>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <td style={baseTd}>
                      {runItem.milestone ? (
                        <Box display="flex" alignItems="center" justifyContent="center" gap="4px">
                          <FlagIcon sx={{ fontSize: 14, color: "#15803d" }} />
                          <Typography fontSize="11px" fontWeight={600} color="#15803d">
                            {runItem.milestone}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography fontSize="12px" color="#d1d5db">
                          —
                        </Typography>
                      )}
                    </td>
                    <td style={baseTd}>
                      <StatusChip status={runItem.tc_status} />
                    </td>
                    <td style={baseTd}>
                      {runItem.testdata ? (
                        <Tooltip title={runItem.testdata} placement="top">
                          <Box
                            sx={{
                              display: "inline-block",
                              padding: "4px 8px",
                              background: "#f0f4ff",
                              border: "1px solid #dbeafe",
                              borderRadius: "6px",
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#1e40af",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "160px",
                              cursor: "pointer",
                              "&:hover": { background: "#eff6ff" },
                            }}
                          >
                            {runItem.testdata}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography fontSize="12px" color="#d1d5db">
                          —
                        </Typography>
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>

      <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
        <Box display="flex" alignItems="center" gap="16px">
          <Box display="flex" alignItems="center" gap="8px">
            <Typography fontSize="12px" color="#6b7280">
              Rows per page:
            </Typography>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: "4px 8px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "12px",
                color: "#374151",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              {[5, 10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </Box>
          <Box display="flex" alignItems="center" gap="8px">
            <Box
              component="button"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              sx={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                background: "#fff",
                cursor: "pointer",
                fontSize: "12px",
                "&:disabled": { opacity: 0.4 },
              }}
            >
              ←
            </Box>
            <Typography fontSize="12px" color="#6b7280" sx={{ minWidth: "80px" }}>
              Page {currentPage} of {totalPages}
            </Typography>
            <Box
              component="button"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
              sx={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                background: "#fff",
                cursor: "pointer",
                fontSize: "12px",
                "&:disabled": { opacity: 0.4 },
              }}
            >
              →
            </Box>
          </Box>
        </Box>
        <Typography fontSize="12px" color="#6b7280">
          Showing {startIdx}–{endIdx} of {totalRecords}
        </Typography>
      </Box>
    </Box>
  );
};

export default MultiRunTable;
